/**
 * PhoneObjectDetector — verify a phone via image processing in hand ROI.
 *
 * EfficientDet-Lite0's 'cell phone' class is weak: it misses phones held
 * edge-on, in shadow, or partially occluded by the hand. Empirically on
 * the 2026-05-22 19:24 demo the user's phone was held in clear view but
 * EfficientDet emitted score 0 (no detection at all).
 *
 * Mirror approach to PenObjectDetector but with phone-shaped target:
 *   - Phones are MODERATELY elongated (1.5–4× aspect, vs pen's ≥5×).
 *   - Phones are larger (mass ≥ 15% of hand ROI vs pen's ~3–10%).
 *   - Phones are dark relative to skin.
 *
 * Output is gated on these constraints AND requires the PhoneGrip hand
 * geometry to be firing — that combination is the segmentation-style
 * cross-check CEO 0520 recommended.
 */

export interface HandLandmark { x: number; y: number; z?: number }

export interface PhoneObjectResult {
  present: boolean;
  confidence: number;
  handBbox: { x: number; y: number; w: number; h: number };
}

const ZERO: PhoneObjectResult = {
  present: false,
  confidence: 0,
  handBbox: { x: 0, y: 0, w: 0, h: 0 },
};

export function detectPhoneObject(
  landmarks: HandLandmark[] | null | undefined,
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
): PhoneObjectResult {
  if (!landmarks || landmarks.length < 21) return ZERO;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return ZERO;

  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const padX = (maxX - minX) * 0.25;
  const padY = (maxY - minY) * 0.25;
  minX = Math.max(0, minX - padX);
  maxX = Math.min(1, maxX + padX);
  minY = Math.max(0, minY - padY);
  maxY = Math.min(1, maxY + padY);
  const handBbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  if (handBbox.w < 0.04 || handBbox.h < 0.04) return ZERO;

  const TARGET_W = 96;
  const aspect = handBbox.h / handBbox.w;
  const sw = TARGET_W;
  const sh = Math.max(32, Math.round(TARGET_W * aspect));
  if (offCanvas.width !== sw || offCanvas.height !== sh) {
    offCanvas.width = sw;
    offCanvas.height = sh;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return ZERO;
  ctx.drawImage(
    video,
    handBbox.x * vw, handBbox.y * vh,
    handBbox.w * vw, handBbox.h * vh,
    0, 0, sw, sh,
  );
  const img = ctx.getImageData(0, 0, sw, sh);

  const gray = new Uint8Array(sw * sh);
  for (let i = 0; i < gray.length; i++) {
    const j = i * 4;
    gray[i] = Math.round(img.data[j] * 0.299 + img.data[j + 1] * 0.587 + img.data[j + 2] * 0.114);
  }

  const sorted = Array.from(gray).sort((a, b) => a - b);
  const pctThresh = sorted[Math.floor(sorted.length * 0.30)] || 100;
  const thresh = Math.min(pctThresh, 110);
  const bin = new Uint8Array(sw * sh);
  for (let i = 0; i < gray.length; i++) bin[i] = gray[i] <= thresh ? 1 : 0;

  // Flood-fill largest dark mass.
  const visited = new Uint8Array(sw * sh);
  let best: number[] = [];
  const stack: number[] = [];
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      const idx = y * sw + x;
      if (bin[idx] !== 1 || visited[idx]) continue;
      const comp: number[] = [];
      stack.length = 0;
      stack.push(idx);
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (visited[cur]) continue;
        visited[cur] = 1;
        comp.push(cur);
        const cx = cur % sw;
        const cy = (cur - cx) / sw;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || nx >= sw || ny < 0 || ny >= sh) continue;
            const nIdx = ny * sw + nx;
            if (bin[nIdx] === 1 && !visited[nIdx]) stack.push(nIdx);
          }
        }
      }
      if (comp.length > best.length) best = comp;
    }
  }

  // Phone mass: substantial fraction of hand ROI (10–65%). Sim showed
  // pen-as-phone false-fire rate exceeded 90% when loosened further, so
  // we keep these strict and rely on the classifier's pinchLike veto.
  const density = best.length / (sw * sh);
  if (density < 0.10) return { ...ZERO, handBbox };
  if (density > 0.65) return { ...ZERO, handBbox };

  let sumX = 0, sumY = 0;
  for (const p of best) {
    sumX += p % sw;
    sumY += (p - (p % sw)) / sw;
  }
  const meanX = sumX / best.length;
  const meanY = sumY / best.length;
  let cxx = 0, cxy = 0, cyy = 0;
  for (const p of best) {
    const px = p % sw;
    const py = (p - px) / sw;
    const dx = px - meanX;
    const dy = py - meanY;
    cxx += dx * dx;
    cxy += dx * dy;
    cyy += dy * dy;
  }
  cxx /= best.length;
  cxy /= best.length;
  cyy /= best.length;
  const trace = cxx + cyy;
  const det = cxx * cyy - cxy * cxy;
  const disc = Math.max(0, trace * trace / 4 - det);
  const sqrtDisc = Math.sqrt(disc);
  const lambda1 = trace / 2 + sqrtDisc;
  const lambda2 = Math.max(0.0001, trace / 2 - sqrtDisc);

  const elongation = Math.max(0, Math.min(1, 1 - lambda2 / lambda1));
  const aspectLW = lambda1 > 0 ? Math.sqrt(lambda1 / lambda2) : 0;

  // Phone: rectangular brick. Moderate elongation (0.30–0.90) and
  // moderate aspect (1.2–4.0). Tight bounds — wider thresholds cause
  // pen-as-phone false-fire over 90 % (verified in sim).
  const elongOk  = elongation >= 0.30 && elongation <= 0.90;
  const aspectOk = aspectLW >= 1.2 && aspectLW <= 4.0;
  const present = elongOk && aspectOk;

  const elongScore  = elongOk  ? 1 - Math.abs(elongation - 0.6) / 0.3 : 0;
  const aspectScore = aspectOk ? 1 - Math.abs(aspectLW - 2.5)  / 1.3  : 0;
  const confidence  = present ? Math.max(0, Math.min(1, 0.4 + 0.6 * elongScore * aspectScore)) : 0;

  return { present, confidence, handBbox };
}
